/**
 * CapacityMeter — one "N active / max" readout with min + over-cap warnings.
 *
 * Both bounds are ENFORCED by the rotation, so the meter says what each one
 * will actually do: over the max, the next update sends the worst formula
 * scores BACK TO THE QUEUE (they did nothing wrong, the cap shrank, and they
 * keep their place in line); at the min, nothing leaves unless the queue has
 * someone to take its place.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { isOverCapacity } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

export interface CapacityMeterProps {
  label: string;
  active: number;
  max: number | null;
  /** Configured live floor; null/undefined = none set for this slot. */
  min?: number | null;
}

/**
 * "at the floor" is worth saying — it explains why Eliminate asks to swap.
 * Below it is a different state and says so: the rotation will not close that
 * gap on its own (a floor guards removals, it does not deploy to fill), so
 * calling it "at minimum" would imply a steady state that nothing maintains.
 */
const FloorNote: FC<{ active: number; min: number }> = ({ active, min }) => {
  if (active > min) return null;
  const below = active < min;
  return (
    <span
      className={below ? 'text-destructive' : 'text-warning'}
      title={
        below
          ? `Below the minimum of ${min}. The rotation will not add paths to close this — ` +
            `promote one, or lower the minimum.`
          : `At the minimum of ${min}. Nothing can be moved off unless a queued ` +
            `path takes its place — you'll be asked to confirm the swap.`
      }
    >
      {' '}
      · {below ? 'below minimum' : 'at minimum'}
    </span>
  );
};

const CapacityMeter: FC<CapacityMeterProps> = ({ label, active, max, min }) => {
  const floor = typeof min === 'number' && min > 0 ? min : null;
  if (max === null) {
    return (
      <span className="text-[10.5px] tabular-nums text-muted-foreground">
        <span className="font-medium text-foreground">{label}</span> {active} ·{' '}
        unlimited
        {floor !== null ? (
          <>
            {' '}
            · min {floor}
            <FloorNote active={active} min={floor} />
          </>
        ) : null}
      </span>
    );
  }
  const over = isOverCapacity(max, active);
  const pct = Math.min(100, (active / Math.max(1, max)) * 100);
  const tone = over ? 'bg-destructive' : pct >= 80 ? 'bg-warning' : 'bg-primary';
  const excess = over ? active - max : 0;
  const floorPct = floor === null ? null : Math.min(100, (floor / Math.max(1, max)) * 100);
  return (
    <div className="min-w-[7rem]">
      <p className="text-[10.5px] tabular-nums">
        <span className="font-medium">{label}</span>{' '}
        <span className={cn(over && 'font-semibold text-destructive')}>
          {active} / {max}
        </span>
        {floor !== null ? <span className="text-muted-foreground"> · min {floor}</span> : null}
        {over ? (
          <span
            className="text-destructive"
            title={`Over capacity: the next optimizer update moves the ${excess} worst (formula score) back to the Queue, at the end of the line.`}
          >
            {' '}
            ⚠ {excess} → queue on next update
          </span>
        ) : null}
        {floor !== null && !over ? <FloorNote active={active} min={floor} /> : null}
      </p>
      <div className="relative mt-0.5 h-1 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn('h-full rounded-full transition-all', tone)}
          style={{ width: `${pct}%` }}
        />
        {floorPct !== null ? (
          <span
            aria-hidden
            title={`Minimum ${floor} live`}
            className="absolute inset-y-0 w-px bg-foreground/50"
            style={{ left: `${floorPct}%` }}
          />
        ) : null}
      </div>
    </div>
  );
};

export default CapacityMeter;
